<template>
  <v-container>
    <v-row class='mb-3'>
      <v-col>
        <strong>Customers can access the portal at https://customer.purtrak.com.</strong>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="9" sm="4">
        <v-autocomplete label="Customers with portal access" v-model="customerId" no-data-text="No customers"
                        @change="$emit('input',customerId)"
                       :items="portalCustomers" item-value="customerId" item-text="customerName" dense outlined hide-details/>
      </v-col>

      <v-col>
        <!-- Add -->
        <v-menu :close-on-content-click="false" bottom right transition="slide-y-transition" nudge-bottom="35" v-model="showAdd">
          <template v-slot:activator="{on}">
            <v-btn icon fab color="primary" small v-on="on" :disabled="eligibleCustomers.length===0"
                   @click="id=eligibleCustomers[0].id">
              <v-icon>mdi-plus-circle</v-icon>
            </v-btn>
          </template>
          <v-card class="pt-4 px-2" width="400">
            <v-autocomplete label="Customer to add" v-model="id"
                    :items="eligibleCustomers" item-value="id" item-text="name" dense outlined hide-details/>
            <v-divider/>
            <v-card-actions class="pr-4">
              <v-spacer/>
              <v-btn plain small color="primary" @click="showAdd=false">Cancel</v-btn>
              <v-btn small color="primary" @click="addCustomer()">Add</v-btn>
            </v-card-actions>
          </v-card>
        </v-menu>

        <!-- Delete -->
        <v-menu :close-on-content-click="false" bottom right transition="slide-y-transition" nudge-bottom="35" v-model="showDelete">
          <template v-slot:activator="{on}">
            <v-btn icon fab color="primary" small v-on="on" :disabled="portalCustomers.length===0"
                   @click="id=customerId">
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </template>
          <v-card class="pt-4 px-2" width="400">
            <div class="mx-2 mb-2">Are you sure you want to delete this customer's access to the portal?</div>
            <v-divider/>
            <v-card-actions class="pr-4">
              <v-spacer/>
              <v-btn plain small color="error" @click="deleteCustomer()">Delete</v-btn>
              <v-btn small color="primary" @click="showDelete=false">Cancel</v-btn>
            </v-card-actions>
          </v-card>
        </v-menu>
      </v-col>
    </v-row>

    <v-row class="mt-4">
      <v-col cols="12" sm="8">
        <outlined-block title="Users with access to the customer's portal" class="mt-4">
          <v-simple-table fixed-header height="calc(100vh - 306px)" class="mt-2">
            <thead>
              <tr>
                <th width="5%">
                  <v-btn fab color="primary" x-small @click="user.name='';user.email='';showAddUser=true" :disabled="!customerId">
                    <v-icon>mdi-plus</v-icon>
                  </v-btn>
                </th>
                <th>Name</th>
                <th>Email</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(user,index) in users" :key="user.id">
                <td><v-btn icon @click.stop="deleteUser(index)"><v-icon small>mdi-delete</v-icon></v-btn></td>
                <td>{{user.name}}</td>
                <td>{{user.email}}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </outlined-block>
      </v-col>
    </v-row>

    <v-dialog v-model="showAddUser" persistent max-width="400">
      <v-card>
        <v-card-title>Add user</v-card-title>
        <v-divider/>
        <v-card-text class="pb-0">
          <v-form ref="addUser">
            <v-container class="mt-2">
              <v-row dense>
                <v-col>
                  <v-text-field label="Name" v-model="user.name" outlined dense persistent-hint
                                hint="Only used if the user does not already exist"/>
                </v-col>
              </v-row>
              <v-row dense>
                <v-col>
                  <v-text-field label="Email" v-model="user.email" :rules="[fieldRules.required,fieldRules.maxLength(50),fieldRules.email]" outlined dense persistent-hint
                                 hint="If user doesn't exist, they will be invited to join PŪRTRAK"/>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>
        <v-divider/>
        <v-card-actions class="pr-6">
          <v-spacer/>
          <v-btn plain color="primary" @click="showAddUser=false">Cancel</v-btn>
          <v-btn color="primary" @click="addUser()">Add</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
export default {
  name: 'AppsPortalCustomers',
  props: {
    portalCustomers: {type:Array, required:true},
    value: {type:String, required:true}
  },
  data() {
    return {
      customerId: this.value,
      allCustomers: [],
      showAdd: false,
      showDelete: false,
      id: '',
      users: [],
      user: {name:'',email:''},
      showAddUser: false
    }
  },
  watch: {
    value() {
      this.customerId = this.value;
      this.getUsers();
    }
  },
  computed: {
    eligibleCustomers() {
      return this.allCustomers.filter(customer => !this.portalCustomers.some(portalCustomer => portalCustomer.customerId === customer.id));
    }
  },
  async created() {
    this.allCustomers = await this.$store.dispatch('customers', true);
  },
  methods: {
    async addCustomer() {
      const portalResults = await this.$apiCall(`mutation AppsPortal($customerId:ID!) {
                                                    portalCustomerAdd(customerId:$customerId)
                                                  }`,
                                              {customerId:this.id}, false, 'portal');
      if (!portalResults.error) {
        this.showAdd = false;
        const customer = this.eligibleCustomers.find(customer => customer.id === this.id);
        this.portalCustomers.push({customerId:this.id,customerName:customer.name});
        this.customerId = this.id;
        this.$emit('input', this.customerId);
      }
    },
    async deleteCustomer() {
      const portalResults = await this.$apiCall(`mutation AppsPortal($customerId:ID!) {
                                                    portalCustomerRemove(customerId:$customerId)
                                                  }`,
                                              {customerId:this.id}, false, 'portal');
      if (!portalResults.error) {
        this.showDelete = false;
        let index = this.portalCustomers.findIndex(customer => customer.customerId === this.id);
        this.portalCustomers.splice(index, 1);
        index = index === this.portalCustomers.length ? index -1 : index;
        this.customerId = this.portalCustomers[index]?.customerId ?? '';
        this.$emit('input', this.customerId);
      }
    },
    async getUsers() {
      if (this.customerId) {
        const results = await this.$apiCall(`query AppsPortal($customerId:ID!) {
                                                portalCustomer(customerId:$customerId) {
                                                  users {userId,name,email}
                                                }
                                            }`,
                                            {customerId:this.customerId}, false, 'portal');
        if (!results.error)
          this.users = results.data.portalCustomer.users;
      }
      else
        this.users = [];
    },
    async addUser() {
      if (!this.$refs.addUser.validate())
        return;
      this.user.customerId = this.customerId;
      const results = await this.$apiCall(`mutation AppsPortal($input:PortalCustomerUserInput!) {
                                             portalCustomerUserAdd(input:$input) {
                                               userId,name,email
                                             }
                                            }`,
                                          {input:this.user}, false, 'portal');
      if (!results.error) {
        this.users.push(results.data.portalCustomerUserAdd);
        this.showAddUser = false;
        this.$store.commit('setError', {type:'success',
                                        message:`${this.user.email} added to the customer portal. (Make sure ${this.user.email} checks their junk email folder.)`});
      }
    },
    async deleteUser(index) {
      const results = await this.$apiCall(`mutation AppsPortalUsers($customerId:ID!,$userId:String!) {
                                             portalCustomerUserRemove(customerId:$customerId,userId:$userId) {userId}
                                            }`,
                                          {customerId:this.customerId,userId:this.users[index].userId}, false, 'portal');
      if (!results.error) {
        this.$store.commit('setError', {type:'success',
                                        message:`${this.users[index].email} removed from the customer portal.`});
        this.users.splice(index, 1);
      }
    }
  }
}
</script>